/**
 * OTA apply policy — where a JS reload is safe vs. costly.
 *
 * Model (enterprise / Apple-like):
 *   • Download aggressively in the background; never block launch.
 *   • Never reload mid-task: check-in, checkout, listing, KYC, **or OTP**.
 *     (Auth OTP was silently applying and wiping in-memory phone handoff +
 *     typed digits — users thought signup failed.)
 *   • Silent apply only on pure idle entry (welcome) and after a real
 *     background stretch on non-critical routes.
 *   • Everywhere else, soft banner so the person chooses the moment.
 */

/** Reload here would discard in-progress work or break auth handoff. */
export const OTA_CRITICAL_ROUTE_PREFIXES = [
  '/checkin',
  // Date/time, pickup point and price are an in-progress booking too. Reloading
  // while someone is choosing them can erase a durable draft between renders;
  // the review gate also defers here so a past-trip prompt never blocks a new
  // reservation before the payment route.
  '/booking',
  '/booking/checkout',
  '/booking/payment-complete',
  '/hosting/new-listing',
  '/profile/verification',
  // Auth OTP / credential entry — phone is staged in memory; reload = lost OTP.
  '/phone-verify',
  '/phone-login',
  '/phone-signup',
  '/verify-phone',
  '/verify-email',
  '/reset-password',
  '/login',
  '/register',
  '/forgot-password',
] as const

/**
 * Superficies donde recargar se siente como abrir la app, no como perder algo.
 *
 * POR QUE SE AMPLIO. Antes solo estaba /welcome, asi que una actualizacion ya
 * descargada se quedaba esperando detras de un banner en TODAS las pantallas
 * donde la gente pasa el tiempo. El reporte fue exacto: "a veces tardan mucho y
 * la gente ya se ha salido de la app" — se salian antes de tocar el banner, y
 * el arreglo llegaba a la sesion siguiente o a ninguna.
 *
 * EL CRITERIO. `reloadAsync` reinicia el bundle: la app arranca de cero y
 * aterriza en su ruta inicial. En la raiz de una pestana eso es invisible —
 * estabas viendo una lista y segues viendo la misma lista, medio segundo
 * despues. En una ruta profunda —el detalle de un carro, una conversacion— te
 * saca de donde estabas, y eso si se siente como un bug.
 *
 * Por eso van las RAICES y no los prefijos: '/explore' aplica, pero
 * '/explore/01ABC' no, porque `matchesPrefix` distingue la ruta exacta de sus
 * hijas solo si se pide asi. Ver OTA_SILENT_APPLY_EXACT_ROUTES.
 */
export const OTA_SILENT_APPLY_ROUTE_PREFIXES = ['/welcome'] as const

/**
 * Raices de pestana: se aplica ahi y NO en sus rutas hijas.
 *
 * Recargar en la lista de explorar devuelve a la lista de explorar. Recargar en
 * el detalle de un carro devuelve a la lista, que es perder el lugar.
 */
export const OTA_SILENT_APPLY_EXACT_ROUTES = [
  '/explore',
  '/trips',
  '/messages',
  '/profile',
  '/hosting',
  '/tours',
] as const

function normalizePath(pathname: string | null | undefined): string {
  if (!pathname) return '/'
  const trimmed = pathname.trim()
  if (!trimmed) return '/'
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
}

function matchesPrefix(pathname: string, prefixes: readonly string[]): boolean {
  const path = normalizePath(pathname)
  return prefixes.some((prefix) => {
    if (path === prefix) return true
    return path.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`)
  })
}

export function isOtaCriticalRoute(pathname: string | null | undefined): boolean {
  return matchesPrefix(pathname ?? '', OTA_CRITICAL_ROUTE_PREFIXES)
}

export function isOtaSilentApplyRoute(pathname: string | null | undefined): boolean {
  const path = normalizePath(pathname)
  if (path === '/' || path === '/(auth)' || path === '/(auth)/') return true
  // Coincidencia EXACTA para las raices de pestana: sus hijas no entran.
  const bare = path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path
  if ((OTA_SILENT_APPLY_EXACT_ROUTES as readonly string[]).includes(bare)) return true
  return matchesPrefix(path, OTA_SILENT_APPLY_ROUTE_PREFIXES)
}

/** Banner is for non-critical, non-silent surfaces (browse, profile, trips). */
export function shouldOfferOtaBanner(pathname: string | null | undefined): boolean {
  if (isOtaCriticalRoute(pathname)) return false
  if (isOtaSilentApplyRoute(pathname)) return false
  return true
}

/**
 * Ventana post-auth en la que NINGUNA recarga silenciosa es aceptable.
 *
 * POR QUÉ EXISTE. `reloadAsync` no es un refresco: es un reinicio completo de
 * la app — splash, re-lectura de sesión y otra ronda de permisos (el diálogo
 * de notificaciones aparece encima del splash). Aplicarlo a los 500 ms de
 * aterrizar en /explore después de un login se ve EXACTAMENTE como "el login
 * se hizo dos veces": el usuario tipeó sus credenciales, vio el spinner, y la
 * app se reinició sola. Grabado en un Redmi con la OTA pendiente de ese día.
 *
 * El problema no es la ruta (explore es un tab root legítimamente silencioso),
 * es el MOMENTO: una autenticación recién completada es tan intocable como una
 * ruta crítica. Pasada la ventana, el apply silencioso vuelve a su curso.
 */
export const OTA_AUTH_GRACE_MS = 120_000

export function isOtaAuthGraceActive(
  lastAuthenticatedAt: number | null | undefined,
  now: number,
): boolean {
  if (lastAuthenticatedAt == null) return false
  return now - lastAuthenticatedAt < OTA_AUTH_GRACE_MS
}

export interface OtaSilentApplyInput {
  pending: boolean
  pathname: string | null | undefined
  /** Una mutación de auth en vuelo (handoff de login/OTP/registro). */
  authInFlight: boolean
  lastAuthenticatedAt: number | null | undefined
  now: number
}

/**
 * Decisión única del apply silencioso: pending + superficie silenciosa +
 * fuera de ruta crítica + fuera de la ventana post-auth + sin auth en vuelo.
 * Pura a propósito — el controller solo la consume y un unit test la fija.
 */
export function shouldSilentlyApplyOta(input: OtaSilentApplyInput): boolean {
  if (!input.pending || input.authInFlight) return false
  if (isOtaCriticalRoute(input.pathname)) return false
  if (isOtaAuthGraceActive(input.lastAuthenticatedAt, input.now)) return false
  return isOtaSilentApplyRoute(input.pathname)
}
